import { useState, useCallback, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import type { GoogleContact, GooglePeopleResponse } from '@/types/googleContacts';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const SCOPES = 'https://www.googleapis.com/auth/contacts.readonly';

export const useGoogleContacts = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [contacts, setContacts] = useState<GoogleContact[]>([]);
  const [hasAccess, setHasAccess] = useState(false);
  const { toast } = useToast();
  const tokenClientRef = useRef<any>(null);
  const accessTokenRef = useRef<string | null>(null);

  // Initialize Google Identity Services
  const initializeGoogleAPI = useCallback(() => {
    return new Promise<void>((resolve, reject) => {
      // Check if Google Client ID is configured
      if (!GOOGLE_CLIENT_ID) {
        reject(new Error('Google Client ID is not configured. Please set VITE_GOOGLE_CLIENT_ID in your .env file.'));
        return;
      }

      // Load the Google Identity Services library
      const script = document.getElementById('google-gis-script');

      if (script || window.google?.accounts) {
        // Already loaded
        if (!tokenClientRef.current) {
          tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
            client_id: GOOGLE_CLIENT_ID,
            scope: SCOPES,
            callback: (response: any) => {
              if (response.access_token) {
                accessTokenRef.current = response.access_token;
                setHasAccess(true);
              }
            },
          });
        }
        resolve();
        return;
      }

      const newScript = document.createElement('script');
      newScript.id = 'google-gis-script';
      newScript.src = 'https://accounts.google.com/gsi/client';
      newScript.async = true;
      newScript.defer = true;
      newScript.onload = () => {
        // Initialize the token client
        tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: SCOPES,
          callback: (response: any) => {
            if (response.access_token) {
              accessTokenRef.current = response.access_token;
              setHasAccess(true);
            }
          },
        });
        resolve();
      };
      newScript.onerror = () => reject(new Error('Failed to load Google Identity Services'));
      document.body.appendChild(newScript);
    });
  }, []);

  // Check if user has already granted access (for this session)
  const checkAccess = useCallback(async () => {
    try {
      await initializeGoogleAPI();
      return !!accessTokenRef.current;
    } catch (error) {
      console.error('Error checking access:', error);
      return false;
    }
  }, [initializeGoogleAPI]);

  // Request access to Google Contacts
  const requestAccess = useCallback(async (): Promise<string> => {
    return new Promise(async (resolve, reject) => {
      try {
        setIsLoading(true);
        
        await initializeGoogleAPI();

        if (!tokenClientRef.current) {
          throw new Error('Token client not initialized');
        }

        // Store the resolve/reject functions to use in the callback
        const callbackHandler = (response: any) => {
          setIsLoading(false);
          
          if (response.error) {
            console.error('OAuth error:', response);
            if (response.error === 'user_cancelled' || response.error === 'popup_closed_by_user') {
              reject({ error: 'popup_closed_by_user' });
            } else {
              reject({ error: response.error });
            }
            return;
          }
          
          if (response.access_token) {
            accessTokenRef.current = response.access_token;
            setHasAccess(true);
            resolve(response.access_token);
          } else {
            reject(new Error('No access token received'));
          }
        };

        // Update the callback
        tokenClientRef.current.callback = callbackHandler;

        // Request access token
        tokenClientRef.current.requestAccessToken({ prompt: 'consent' });
      } catch (error) {
        setIsLoading(false);
        reject(error);
      }
    });
  }, [initializeGoogleAPI]);

  // Fetch contacts from Google People API
  const fetchContacts = useCallback(async (pageToken?: string): Promise<GooglePeopleResponse> => {
    try {
      if (!accessTokenRef.current) {
        throw new Error('No access token available');
      }

      const params = new URLSearchParams({
        personFields: 'names,emailAddresses,phoneNumbers,photos,organizations',
        pageSize: '1000',
        ...(pageToken && { pageToken }),
      });

      const response = await fetch(
        `https://people.googleapis.com/v1/people/me/connections?${params}`,
        {
          headers: {
            Authorization: `Bearer ${accessTokenRef.current}`,
          },
        }
      );

      if (response.status === 401) {
        // Token expired, clear it
        accessTokenRef.current = null;
        setHasAccess(false);
        throw new Error('Access token expired');
      }

      if (!response.ok) {
        throw new Error('Failed to fetch contacts');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast({
        title: 'Failed to fetch contacts',
        description: 'Unable to retrieve your Google contacts. Please try again.',
        variant: 'destructive',
      });
      throw error;
    }
  }, [toast]);

  // Load all contacts
  const loadContacts = useCallback(async () => {
    try {
      setIsLoading(true);
      const allContacts: GoogleContact[] = [];
      let nextPageToken: string | undefined;

      // Check if we have access first
      const hasCurrentAccess = await checkAccess();
      if (!hasCurrentAccess) {
        await requestAccess();
      }

      // Fetch all pages of contacts
      do {
        const response = await fetchContacts(nextPageToken);
        if (response.connections) {
          allContacts.push(...response.connections);
        }
        nextPageToken = response.nextPageToken;
      } while (nextPageToken);

      setContacts(allContacts);
      return allContacts;
    } catch (error: any) {
      console.error('Error loading contacts:', error);
      
      // Re-throw specific errors for the component to handle
      if (error?.error === 'popup_closed_by_user' || error?.error === 'access_denied') {
        throw error;
      }
      
      // For other errors, return empty array
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [checkAccess, requestAccess, fetchContacts]);

  return {
    contacts,
    isLoading,
    hasAccess,
    checkAccess,
    requestAccess,
    loadContacts,
  };
};

// Type declaration for Google Identity Services
declare global {
  interface Window {
    google: any;
  }
}